import React, { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { Calendar, dateFnsLocalizer } from 'react-big-calendar';
import withDragAndDrop from 'react-big-calendar/lib/addons/dragAndDrop';
import format from 'date-fns/format';
import parse from 'date-fns/parse';
import startOfWeek from 'date-fns/startOfWeek';
import getDay from 'date-fns/getDay';
import es from 'date-fns/locale/es';
import { format as formatDate } from 'date-fns';
import { citasService } from '../api/citas';
import { pacientesService } from '../api/pacientes';
import { tratamientosService } from '../api/tratamientos';
import { useWhatsApp } from '../context/WhatsAppContext';
import '../components/citas/CalendarioCitas.css';
import axiosInstance from '../api/axios';
import 'react-big-calendar/lib/addons/dragAndDrop/styles.css';

import MobileCalendarWrapper from '../components/calendar/MobileCalendarWrapper';
import AddButton from '../components/ui/AddButton';
import useDashboardStore from '../store/dashboardStore';
import useUIStore from '../store/uiStore';
import CitaDetailModal from '../components/citas/CitaDetailModal';
import CitaFormModal from '../components/citas/CitaFormModal';

// Wrap Calendar with Drag and Drop HOC
const DnDCalendar = withDragAndDrop(Calendar);

const locales = {
  'es': es,
};

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales,
});

const AdminCitasPage = () => {
  const [citas, setCitas] = useState([]);
  const [pacientes, setPacientes] = useState([]);
  const [tratamientos, setTratamientos] = useState([]);
  const [profesionales, setProfesionales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [horariosDisponibles, setHorariosDisponibles] = useState([]);
  const { enviarConfirmacionCita } = useWhatsApp();
  const [currentView, setCurrentView] = useState('month');
  
  // Conectar al store global de fechas (sincronizado con Dashboard y Rentabilidad)
  const { selectedDate: currentDate, setSelectedDate: setCurrentDate } = useDashboardStore();
  const { openQuickBooking } = useUIStore();
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedCita, setSelectedCita] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showCompletarConfirm, setShowCompletarConfirm] = useState(false);
  const [estadoPendiente, setEstadoPendiente] = useState(null);
  const [showRedirectToFicha, setShowRedirectToFicha] = useState(false);
  const navigate = useNavigate();
  const [modalOpen, setModalOpen] = useState(false);

  // useEffect

  const [editFormData, setEditFormData] = useState({
    id: null,
    paciente_rut: '',
    fecha: '',
    hora: '',
    tipo_tratamiento: '',
    estado: '',
    original_hora: '',
  });

  const [formData, setFormData] = useState({
    paciente_rut: '',
    fecha: '',
    hora: '',
    tipo_tratamiento: '',
    profesional: '', // ID del profesional asignado (opcional)
    // Sprint 4: Planes de sesiones
    plan_sesiones_total: null,
    plan_descuento: null,
  });

  const [horasOcupadas, setHorasOcupadas] = useState([]);

  // Memoizar la función cargarDatos
  const cargarDatos = useCallback(async () => {
    try {
      // Usar el endpoint de depuración para obtener las citas
      const [citasRes, pacientesRes, tratamientosRes, profesionalesRes] = await Promise.all([
        citasService.getAll(),
        pacientesService.getAll(),
        tratamientosService.getAll(),
        axiosInstance.get('/usuarios/')
      ]);

      let citasData = [];

      // Procesar los datos de citas desde endpoint autenticado
      if (citasRes.data) {
        const payload = citasRes.data;
        if (Array.isArray(payload)) {
          citasData = payload;
        } else if (Array.isArray(payload?.results)) {
          citasData = payload.results;
        } else if (Array.isArray(payload?.citas)) {
          citasData = payload.citas;
        }
      }

      // Verificar y formatear cada cita
      const citasFormateadas = citasData.map(cita => {
        if (!cita.id || !cita.fecha || !cita.hora) {
          console.warn('Cita con datos incompletos:', cita);
          return null;
        }

        // Asegurarse de que la hora tenga el formato correcto (HH:MM)
        let horaFormateada = cita.hora;
        if (horaFormateada && horaFormateada.includes(':')) {
          const partes = horaFormateada.split(':');
          if (partes.length >= 2) {
            horaFormateada = `${partes[0]}:${partes[1]}`;
          }
        }

        return {
          ...cita,
          fecha: cita.fecha,
          hora: horaFormateada,
          paciente_nombre: cita.paciente_nombre || 'Sin nombre',
          tipo_tratamiento: cita.tratamiento_nombre || cita.tipo_tratamiento || 'Sin tratamiento'
        };
      }).filter(cita => cita !== null);

      // Establecer las citas en el estado
      setCitas(citasFormateadas);

      // Establecer los pacientes
      if (Array.isArray(pacientesRes.data)) {
        setPacientes(pacientesRes.data);
      } else {
        console.warn('Datos de pacientes no válidos:', pacientesRes.data);
        setPacientes([]);
      }

      // Establecer los tratamientos
      if (Array.isArray(tratamientosRes.data)) {
        setTratamientos(tratamientosRes.data);
      } else {
        setTratamientos([]);
      }

      // Establecer los profesionales (miembros del equipo)
      if (Array.isArray(profesionalesRes.data)) {
        setProfesionales(profesionalesRes.data);
      } else {
        setProfesionales([]);
      }

    } catch (error) {
      console.error('Error al cargar datos:', error);
      if (error.response) {
        console.error('Detalles del error:', error.response.data);
      }
      setCitas([]);
      setPacientes([]);
      notify('Error al cargar las citas', 'error');
    } finally {
      setLoading(false);
    }
  }, []); // No hay dependencias ya que usa solo funciones estables

  useEffect(() => {
    cargarDatos();
  }, [cargarDatos]); // Solo depender de cargarDatos

  const cargarHorariosDisponibles = async (fecha, citaId = null) => {
    try {
      const response = await axiosInstance.get(`/citas/horarios-disponibles/?fecha=${fecha}&cita_id=${citaId || ''}`);
      setHorariosDisponibles(response.data.horarios_disponibles);
      setHorasOcupadas(response.data.horas_ocupadas);
    } catch (error) {
      console.error('Error al cargar horarios disponibles:', error);
      notify('Error al cargar horarios disponibles', 'error');
    }
  };

  const handleSelectSlot = async (slotInfo) => {

    const start = new Date(slotInfo.start);
    setSelectedDate(start);

    // Formatear la fecha para la API
    const fechaFormateada = format(start, 'yyyy-MM-dd');
    setFormData(prev => ({
      ...prev,
      fecha: fechaFormateada
    }));

    setShowForm(true);

    try {
      // Primero establecer un estado de carga
      setHorariosDisponibles([]);
      setHorasOcupadas([]);

      // Obtener los horarios disponibles y las citas existentes en paralelo
      const [horariosResponse, citasResponse] = await Promise.all([
        citasService.getHorariosDisponibles(fechaFormateada),
        citasService.getByFecha(fechaFormateada)
      ]);

      // Procesar las citas para obtener las horas ocupadas
      let todasHorasOcupadas = [];
      if (citasResponse.data && Array.isArray(citasResponse.data)) {
        citasResponse.data.forEach(cita => {
          // Normalizar el formato de hora
          let hora = cita.hora;
          if (hora && hora.includes(':')) {
            const partes = hora.split(':');
            if (partes.length >= 2) {
              hora = `${partes[0]}:${partes[1]}`;
            }
          }

          // Añadir la hora principal
          todasHorasOcupadas.push(hora);

          // Si la cita tiene duración extendida, añadir la hora siguiente
          if ((cita.tratamiento?.duracion_minutos || 60) >= 120) {
            const [horaH, horaM] = hora.split(':').map(Number);
            const siguienteHora = `${(horaH + 1).toString().padStart(2, '0')}:${horaM.toString().padStart(2, '0')}`;
            todasHorasOcupadas.push(siguienteHora);
          }
        });
      }

      // Actualizar el estado de horas ocupadas
      setHorasOcupadas(todasHorasOcupadas);

      // Procesar los horarios disponibles
      if (horariosResponse.data && horariosResponse.data.horas_disponibles) {
        const horariosDisponiblesFiltrados = horariosResponse.data.horas_disponibles.filter(
          hora => !todasHorasOcupadas.includes(hora)
        );
        setHorariosDisponibles(horariosDisponiblesFiltrados);
      } else {
        // Si no hay respuesta del backend, usar horarios por defecto
        const horariosGenerados = [];
        for (let hora = 9; hora <= 18; hora++) {
          const horaStr = `${hora.toString().padStart(2, '0')}:00`;
          if (!todasHorasOcupadas.includes(horaStr)) {
            horariosGenerados.push(horaStr);
          }
        }
        setHorariosDisponibles(horariosGenerados);
      }

    } catch (error) {
      console.error('Error al cargar horarios:', error);
      notify('Error al cargar los horarios disponibles', 'error');

      // En caso de error, mostrar horarios por defecto
      const horariosGenerados = [];
      for (let hora = 9; hora <= 18; hora++) {
        horariosGenerados.push(`${hora.toString().padStart(2, '0')}:00`);
      }
      setHorariosDisponibles(horariosGenerados);
    }
  };

  const handleSelectEvent = (event) => {
    // Buscar la cita completa por ID
    const cita = citas.find(c => c.id === event.id);
    if (cita) {
      setSelectedCita(cita);
      setShowDetailModal(true);
    }
  };

  const handleViewChange = (view) => {
    setCurrentView(view);
    
    // Scroll al calendario en móvil cuando cambie la vista
    if (window.innerWidth < 768) {
      setTimeout(() => {
        const calendarElement = document.querySelector('.rbc-calendar');
        if (calendarElement) {
          calendarElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    }
  };

  const handleNavigate = (date) => {
    setCurrentDate(date); // Actualiza el store global
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    let loadingToast;
    try {
      // Validaciones básicas
      if (!formData.paciente_rut || !formData.tipo_tratamiento || !formData.hora) {
        notify('Por favor, complete todos los campos requeridos', 'error');
        return;
      }

      // Preparar los datos para enviar
      const datosParaEnviar = {
        ...formData,
        // Solo enviar profesional si se seleccionó uno específico
        profesional: formData.profesional ? parseInt(formData.profesional) : null
      };

      // Mostrar mensaje de carga con toast (sin DOM manual = sin leaks)
      loadingToast = toast.loading('Creando cita...', {
        icon: '⏳',
        style: { background: '#3b82f6', color: '#fff' },
      });

      // Crear la cita
      const response = await citasService.create(datosParaEnviar);

      // Procesar la respuesta y actualizar la UI
      if (response.data) {
        setShowForm(false);
        await cargarDatos();
        notify('Cita creada con éxito', 'success');

        // Limpiar el formulario
        setFormData({
          paciente_rut: '',
          fecha: '',
          hora: '',
          tipo_tratamiento: '',
          profesional: ''
        });
      }
    } catch (error) {
      console.error('Error al crear cita:', error);
      
      // Manejar bloqueo Freemium (403 Forbidden)
      if (error.response && error.response.status === 403) {
        setShowForm(false);
        
        const mensaje = error.response.data?.detail || "Has alcanzado el límite de 1 cita gratuita. Suscríbete para continuar.";
        
        toast.error(mensaje, {
          duration: 6000,
          icon: '🔒',
          style: {
            border: '1px solid #E2E8F0',
            padding: '16px',
            color: '#1e293b',
          },
        });
      } else {
        notify('Error al crear la cita', 'error');
      }
    } finally {
      toast.dismiss(loadingToast);
    }
  };

  // Función para mostrar notificaciones — usa react-hot-toast (no DOM manual = sin memory leaks)
  const notify = (mensaje, tipo = 'default') => {
    const opts = {
      success: { icon: '✅', style: { background: '#10b981', color: '#fff' } },
      error: { icon: '❌', style: { background: '#ef4444', color: '#fff' } },
      default: { icon: 'ℹ️', style: { background: '#3b82f6', color: '#fff' } },
    };
    const cfg = opts[tipo] || opts.default;
    toast(mensaje, { icon: cfg.icon, style: cfg.style, duration: 3000 });
  };

  // Función para editar una cita
  const handleEditClick = async (cita) => {

    // Normalizar el formato de hora
    let horaFormateada = cita.hora;
    if (horaFormateada && horaFormateada.includes(':')) {
      const partes = horaFormateada.split(':');
      if (partes.length >= 2) {
        horaFormateada = `${partes[0]}:${partes[1]}`;
      }
    }

    // Determinar si la cita tiene duración extendida basada en el tratamiento
    const duracionMinutos = cita.tratamiento?.duracion_minutos || 60;

    // Preparar datos para la edición
    const formDataEdit = {
      id: cita.id,
      paciente_rut: cita.paciente_rut,
      fecha: cita.fecha,
      hora: horaFormateada,
      tipo_tratamiento: cita.tipo_tratamiento || cita.tratamiento_nombre,
      estado: cita.estado,
      original_hora: horaFormateada,
    };

    setEditFormData(formDataEdit);

    // Cargar horarios disponibles para la fecha incluyendo la hora actual
    handleSelectSlotEdit({
      start: new Date(`${cita.fecha}T12:00:00`),
      citaId: cita.id,
      hora: horaFormateada,
      duracionMinutos: duracionMinutos
    });

    // Mostrar el modal de edición
    setShowEditModal(true);
  };

  // Función especial para cargar horarios en modo edición
  const handleSelectSlotEdit = async (slotInfo) => {

    const start = new Date(slotInfo.start);
    setSelectedDate(start);

    // Formatear la fecha para la API
    const fechaFormateada = format(start, 'yyyy-MM-dd');

    try {
      // Generamos horarios fijos entre 9:00 y 18:00 como fallback
      const horariosGenerados = [];
      for (let hora = 9; hora <= 18; hora++) {
        horariosGenerados.push(`${hora.toString().padStart(2, '0')}:00`);
      }

      setHorariosDisponibles(horariosGenerados);

      // Luego intentamos obtener los horarios reales del backend
      try {
        const response = await citasService.getHorariosDisponibles(fechaFormateada);

        if (response.data && response.data.horas_disponibles) {
          // Incluir la hora original en los horarios disponibles
          let horarios = response.data.horas_disponibles;

          // Normalizar horarios recibidos (eliminar segundos si existen)
          horarios = horarios.map(hora => {
            if (hora && hora.includes(':')) {
              const partes = hora.split(':');
              if (partes.length >= 2) {
                return `${partes[0]}:${partes[1]}`;
              }
            }
            return hora;
          });

          // Si hay una hora original que no está en la lista, la añadimos
          let horaOriginal = slotInfo.hora;
          if (horaOriginal && horaOriginal.includes(':')) {
            const partes = horaOriginal.split(':');
            if (partes.length >= 2) {
              horaOriginal = `${partes[0]}:${partes[1]}`;
            }
          }

          // Si la cita tiene duración extendida, también necesitamos incluir la siguiente hora
          if ((slotInfo.duracionMinutos || 60) >= 120) {
            const [horaH, horaM] = horaOriginal.split(':').map(Number);
            const siguienteHora = `${(horaH + 1).toString().padStart(2, '0')}:${horaM.toString().padStart(2, '0')}`;

            // Añadir tanto la hora original como la siguiente hora
            if (!horarios.includes(horaOriginal)) {
              horarios.push(horaOriginal);
            }
            if (!horarios.includes(siguienteHora)) {
              horarios.push(siguienteHora);
            }
            // Ordenar los horarios
            horarios.sort();
          } else {
            // Si no tiene duración extendida, solo añadir la hora original si no está
            if (!horarios.includes(horaOriginal)) {
              horarios.push(horaOriginal);
              // Ordenar los horarios
              horarios.sort();
            }
          }

          setHorariosDisponibles(horarios);

          // También cargamos las horas ocupadas para mostrarlas como no disponibles
          try {
            const citasResponse = await citasService.getByFecha(fechaFormateada);

            if (citasResponse.data && Array.isArray(citasResponse.data)) {
              // Inicializar array para todas las horas ocupadas
              let todasHorasOcupadas = new Set();

              // Filtrar las citas que no sean la que estamos editando
              citasResponse.data
                .filter(c => c.id !== slotInfo.citaId)
                .forEach(c => {
                  // Normalizar formato de hora
                  let hora = c.hora;
                  if (hora && hora.includes(':')) {
                    const partes = hora.split(':');
                    if (partes.length >= 2) {
                      hora = `${partes[0]}:${partes[1]}`;
                    }
                  }

                  // Añadir la hora principal
                  todasHorasOcupadas.add(hora);

                  // Si la cita tiene duración extendida, añadir la siguiente hora también
                  if ((c.tratamiento?.duracion_minutos || 60) >= 120) {
                    const [horaH, horaM] = hora.split(':').map(Number);
                    const siguienteHora = `${(horaH + 1).toString().padStart(2, '0')}:${horaM.toString().padStart(2, '0')}`;
                    todasHorasOcupadas.add(siguienteHora);
                  }
                });

              // Guardar las horas ocupadas en state pero preservar la hora original y permitir su selección
              setHorasOcupadas(Array.from(todasHorasOcupadas));
            }
          } catch (error) {
            console.error("Error al cargar citas para la fecha:", error);
          }
        }
      } catch (apiError) {
        console.error('Error al obtener horarios desde la API para edición:', apiError);
      }
    } catch (error) {
      console.error('Error general al procesar la selección de fecha:', error);
      toast.error('Error al preparar el formulario de edición');
    }
  };

  // Función para actualizar una cita
  const handleUpdateCita = async (e) => {
    e.preventDefault();

    let loadingToast;
    try {
      loadingToast = toast.loading('Actualizando cita...', { icon: '⏳', style: { background: '#3b82f6', color: '#fff' } });

      // Hacer una copia de los datos para enviar al backend
      const datosCita = {
        id: editFormData.id,
        paciente_rut: editFormData.paciente_rut,
        tipo_tratamiento: editFormData.tipo_tratamiento,
        fecha: editFormData.fecha,
        hora: editFormData.hora, // Añadir la hora que faltaba
        estado: editFormData.estado
      };

      // Hacer la petición de actualización
      const response = await citasService.update(editFormData.id, datosCita);

      // Cerrar el modal y recargar los datos
      setShowEditModal(false);

      // Actualizar la información en la lista local de citas
      setCitas(prevCitas => prevCitas.map(cita =>
        cita.id === editFormData.id
          ? {
            ...cita,
            ...datosCita,
            paciente_nombre: pacientes.find(p => p.rut === datosCita.paciente_rut)?.nombre || cita.paciente_nombre
          }
          : cita
      ));

      // Actualizar datos completos
      await cargarDatos();

      toast.dismiss(loadingToast);
    notify('Cita actualizada correctamente', 'success');

    } catch (error) {
      console.error('Error al actualizar cita:', error);
      notify('Error al actualizar la cita', 'error');
    }
  };

  // Función para confirmar eliminación
  const handleDeleteConfirm = (cita) => {
    setSelectedCita(cita);
    setShowDeleteConfirm(true);
  };

  // Función para eliminar una cita
  const handleDeleteCita = async () => {
    if (!selectedCita || !selectedCita.id) {
      notify('No se ha seleccionado una cita para eliminar', 'error');
      return;
    }

    const loadingToast = toast.loading('Eliminando cita...', { icon: '⏳', style: { background: '#3b82f6', color: '#fff' } });

    try {
      const response = await citasService.delete(selectedCita.id);

      // Cerrar modal y recargar datos
      setShowDeleteConfirm(false);
      await cargarDatos();

      toast.dismiss(loadingToast);
    notify('Cita eliminada correctamente', 'success');
    } catch (error) {
      console.error('Error al eliminar cita:', error);

      // Mostrar mensaje de error detallado
      let mensaje = 'Error al eliminar la cita';

      if (error.response) {
        // Detalles del error de respuesta
        if (error.response.status === 401) {
          mensaje = 'Error de autenticación. Por favor, refresca la página e intenta nuevamente.';
        } else if (error.response.status === 403) {
          mensaje = 'No tienes permiso para eliminar esta cita. Error de CSRF o autenticación.';
        } else if (error.response.status === 404) {
          mensaje = 'La cita que intentas eliminar no existe o ya fue eliminada.';
        } else if (error.response.status === 400) {
          mensaje = 'Datos inválidos. Verifica la información de la cita.';
          if (error.response.data && typeof error.response.data === 'object') {
            const errores = Object.entries(error.response.data)
              .map(([key, value]) => `${key}: ${value}`)
              .join(', ');
            mensaje += ` (${errores})`;
          }
        }
      }

      notify(mensaje, 'error');
    }
  };

  // Función para cambio rápido de estado desde el modal de detalles
  const handleQuickStateChange = async (nuevoEstado) => {
    if (!selectedCita) return;
    
    // Si se va a completar, mostrar confirmación
    if (nuevoEstado === 'completada' && selectedCita.estado !== 'completada') {
      setEstadoPendiente(nuevoEstado);
      setShowCompletarConfirm(true);
      return;
    }
    
    await ejecutarCambioEstado(nuevoEstado);
  };

  // Ejecutar el cambio de estado (usado por confirmación y cambio directo)
  const ejecutarCambioEstado = async (nuevoEstado) => {
    if (!selectedCita) return;
    
    const loadingToast = toast.loading('Actualizando estado...', { icon: '⏳', style: { background: '#3b82f6', color: '#fff' } });
    
    try {
      const datosCita = {
        id: selectedCita.id,
        estado: nuevoEstado
      };
      
      await citasService.update(selectedCita.id, datosCita);
      
      // Actualizar la cita seleccionada localmente
      const citaActualizada = { ...selectedCita, estado: nuevoEstado };
      setSelectedCita(citaActualizada);
      
      // Actualizar la lista de citas
      setCitas(prevCitas => prevCitas.map(cita =>
        cita.id === selectedCita.id ? citaActualizada : cita
      ));
      
      // Recargar datos para obtener costo_real actualizado si es completada
      if (nuevoEstado === 'completada') {
        await cargarDatos();
        // Re-seleccionar la cita actualizada
        const citasActualizadas = await citasService.getAll();
        const citaRefrescada = citasActualizadas.data.find(c => c.id === selectedCita.id);
        if (citaRefrescada) {
          setSelectedCita(citaRefrescada);
        }
      }
      
      toast.dismiss(loadingToast);
    const estadoLabel = {
        'reservada': 'Reservada',
        'confirmada': 'Confirmada', 
        'completada': 'Completada',
        'cancelada': 'Cancelada'
      };
      
      notify(`Cita marcada como ${estadoLabel[nuevoEstado]}`, 'success');
      
      // Si se completó, mostrar modal para crear ficha
      if (nuevoEstado === 'completada') {
        setShowRedirectToFicha(true);
      }
      
    } catch (error) {
      console.error('Error al cambiar estado:', error);
      toast.dismiss(loadingToast);
    notify('Error al cambiar el estado de la cita', 'error');
    }
    
    setShowCompletarConfirm(false);
    setEstadoPendiente(null);
  };

  const getDuracionFromTratamiento = (formData) => {
    const t = tratamientos.find(tr => tr.id === formData.tratamiento_id);
    return t?.duracion_minutos || 60;
  };

  // Función para manejar cambio de estado de cita
  useEffect(() => {
    // Cualquier modal abierto detiene la actualización automática
    const isAnyModalOpen = showForm || showEditModal || showDeleteConfirm || showDetailModal;
    setModalOpen(isAnyModalOpen);
  }, [showForm, showEditModal, showDeleteConfirm, showDetailModal]);

  // ============================================
  // DRAG & DROP - Mover citas con Optimistic UI
  // ============================================
  const moveEvent = useCallback(async ({ event, start, end }) => {
    // 1. Guardar estado previo para rollback
    const previousCitas = [...citas];
    
    // 2. Optimistic UI - actualizar el evento inmediatamente
    const updatedCitas = citas.map(cita => {
      if (cita.id === event.id) {
        return {
          ...cita,
          fecha: format(start, 'yyyy-MM-dd'),
          hora: format(start, 'HH:mm'),
        };
      }
      return cita;
    });
    setCitas(updatedCitas);
    
    // 3. Preparar datos para API
    const fecha = format(start, 'yyyy-MM-dd');
    const hora = format(start, 'HH:mm');
    
    try {
      // 4. PATCH request - solo enviar fecha y hora
      await citasService.patchReschedule(event.id, fecha, hora);
      
      notify('Cita reagendada correctamente', 'success');
      
      // Recargar datos para asegurar sincronización
      cargarDatos();
    } catch (error) {
      // 5. Rollback en caso de error
      console.error('Error al reagendar cita:', error);
      setCitas(previousCitas);
      
      const errorMessage = error.response?.data?.detail || 
                          error.response?.data?.message || 
                          'Horario no disponible o error de conexión';
      notify(`Error al reagendar: ${errorMessage}`, 'error');
    }
  }, [citas, cargarDatos]);

  if (loading) {
    return <div className="calendario-loading">Cargando...</div>;
  }

  // Eventos para el calendario
  const eventos = Array.isArray(citas) ? citas.map(cita => {
    try {
      // Verificar datos críticos
      if (!cita.fecha || !cita.hora) {
        console.error('Cita sin fecha u hora:', cita);
        return null;
      }

      // Prevenir errores de formato
      const fechaStr = typeof cita.fecha === 'string' ? cita.fecha : cita.fecha.toString();
      const horaStr = typeof cita.hora === 'string' ? cita.hora : cita.hora.toString();

      // Construir fechas correctamente
      const fechaHora = `${fechaStr}T${horaStr}`;

      // Calcular la duración en minutos (derivada del tratamiento)
      const duracionMinutos = cita.tratamiento?.duracion_minutos || 60;

      // Determinar el color según el tipo de tratamiento
      let backgroundColor = '#3788d8'; // color por defecto

      const nombreTratamiento = (cita.tipo_tratamiento || cita.tratamiento_nombre || '').toLowerCase();
      if (nombreTratamiento.includes('manicura')) {
        backgroundColor = '#ec4899'; // Color rosa para manicura (pink-500 en tailwind)
      } else {
        // Para citas podológicas, siempre usar azul
        backgroundColor = '#3b82f6'; // Azul (blue-500 en tailwind)
      }

      const evento = {
        id: cita.id,
        title: `${cita.paciente_nombre || 'Sin nombre'} - ${cita.tipo_tratamiento || cita.tratamiento_nombre || 'Sin tratamiento'}${duracionMinutos >= 120 ? ' (2h)' : ''}${cita.plan_sesiones_total ? ` | Sesión ${cita.plan_sesion_actual}/${cita.plan_sesiones_total}` : ''}`,
        start: new Date(fechaHora),
        end: new Date(new Date(fechaHora).getTime() + duracionMinutos * 60000),
        resource: {
          ...cita,
          duracion_cita: duracionMinutos
        },
        backgroundColor: backgroundColor,
      };

      // Verificar que las fechas se crearon correctamente
      if (isNaN(evento.start.getTime()) || isNaN(evento.end.getTime())) {
        console.error('Error: Fecha inválida para el evento', { cita, fechaHora });
        return null;
      }

      return evento;
    } catch (error) {
      console.error('Error al procesar cita:', error, cita);
      return null;
    }
  }).filter(evento => evento !== null) : [];

  // Añadir un eventPropGetter para el calendario - Colores por tipo de servicio
  const eventPropGetter = (event, start, end, isSelected) => {
    // Valores por defecto - Teal para Podología
    let backgroundColor = '#2A9D8F'; // primary - Teal (Podología)
    let borderColor = '#227f74';     // primary-dark

    if (event) {
      // Normalizamos el texto para buscar palabras clave sin importar mayúsculas
      const titulo = (event.title || '').toLowerCase();
      const tipoTratamiento = (event.resource?.tipo_tratamiento || '').toLowerCase();
      const texto = `${titulo} ${tipoTratamiento}`;

      // Manicura / Belleza - Coral de Acento
      if (texto.includes('manicura') || texto.includes('esmaltado')) {
        backgroundColor = '#E76F51'; // accent - Coral (Manicura)
        borderColor = '#c75b41';     // accent-dark
      }
      // Se pueden agregar más categorías aquí:
      // else if (texto.includes('masaje') || texto.includes('facial')) {
      //   backgroundColor = '#E9C46A'; // Dorado (Spa/Bienestar)
      //   borderColor = '#d4a84d';
      // }
    }

    return {
      style: {
        backgroundColor: backgroundColor,
        borderLeft: `4px solid ${borderColor}`,
        borderRadius: '6px',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
        opacity: isSelected ? 1 : 0.95,
        color: 'white',
        border: 'none',
        display: 'block',
        padding: '4px 8px',
        fontSize: '0.8125rem',
        fontWeight: '500',
        transition: 'all 0.2s ease',
        cursor: 'pointer',
      }
    };
  };

  // Ordenar citas por fecha y hora para la vista de agenda
  const citasOrdenadas = Array.isArray(citas) ? [...citas].sort((a, b) => {
    try {
      // Primero ordenar por fecha
      const fechaA = new Date(`${a.fecha}T${a.hora}`);
      const fechaB = new Date(`${b.fecha}T${b.hora}`);

      // Verificar si las fechas son válidas
      if (isNaN(fechaA.getTime())) {
        console.warn('Fecha inválida:', a);
        return 1; // Colocar elementos con fechas inválidas al final
      }
      if (isNaN(fechaB.getTime())) {
        console.warn('Fecha inválida:', b);
        return -1;
      }

      return fechaA - fechaB;
    } catch (error) {
      console.error('Error al ordenar citas:', error);
      return 0;
    }
  }) : [];

  // Componente para mostrar cuando no hay citas
  const NoCitas = () => (
    <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-4">
      <div className="flex items-start">
        <div className="flex-shrink-0">
          <svg className="h-5 w-5 text-yellow-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
        </div>
        <div className="ml-3">
          <p className="text-sm text-yellow-700">
            No hay citas registradas. Use el botón "Verificar Citas" para depurar problemas o cree una nueva cita con el botón "Nueva Cita".
          </p>
        </div>
      </div>
    </div>
  );

  return (
    <div className="p-6">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-6 space-y-4 sm:space-y-0">
        <div className="flex items-center">
          <h1 className="text-xl sm:text-2xl font-bold text-gray-800">
            <span className="hidden sm:inline">Calendario de Citas</span>
            <span className="sm:hidden">
              Citas - {currentView === 'month' ? 'Mes' : 
                      currentView === 'week' ? 'Semana' : 
                      currentView === 'day' ? 'Día' : 'Lista'}
            </span>
          </h1>
        </div>
        <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-4">

          
          {/* Botones de vista para móvil */}
          <div className="flex space-x-2 lg:hidden">
            <button
              className={`flex-1 px-2 py-2 rounded-md transition-colors text-xs font-medium ${currentView === 'month' ? 'bg-indigo-700 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
              style={{ minHeight: '44px' }}
              onClick={() => setCurrentView('month')}
            >
              Mes
            </button>
            <button
              className={`flex-1 px-2 py-2 rounded-md transition-colors text-xs font-medium ${currentView === 'week' ? 'bg-indigo-700 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
              style={{ minHeight: '44px' }}
              onClick={() => setCurrentView('week')}
            >
              Semana
            </button>
            <button
              className={`flex-1 px-2 py-2 rounded-md transition-colors text-xs font-medium ${currentView === 'day' ? 'bg-indigo-700 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
              style={{ minHeight: '44px' }}
              onClick={() => setCurrentView('day')}
            >
              Día
            </button>
            <button
              className={`flex-1 px-2 py-2 rounded-md transition-colors text-xs font-medium ${currentView === 'agenda' ? 'bg-indigo-700 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
              style={{ minHeight: '44px' }}
              onClick={() => setCurrentView('agenda')}
            >
              Lista
            </button>
          </div>
          
          {/* Botón Ver Agenda para desktop */}
          <button
            className={`hidden lg:block w-full sm:w-auto px-3 py-2 sm:px-4 rounded-md transition-colors text-sm font-medium ${currentView === 'agenda' ? 'bg-indigo-700 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            style={{ minHeight: '44px' }}
            onClick={() => setCurrentView('agenda')}
          >
            <span className="hidden sm:inline">Ver Agenda</span>
            <span className="sm:hidden">Agenda</span>
          </button>
          <AddButton
            onClick={() => {
              setSelectedDate(new Date());
              setFormData(prev => ({ ...prev, fecha: format(new Date(), 'yyyy-MM-dd') }));
              handleSelectSlot({ start: new Date() });
            }}
            className="w-full sm:w-auto"
          >
            Nueva Cita
          </AddButton>
          

        </div>
      </div>

      <div className="bg-white p-4 sm:p-6 rounded-lg shadow mb-6">
        <MobileCalendarWrapper onDateSelect={handleSelectSlot}>
        <DnDCalendar
          localizer={localizer}
          events={eventos}
          startAccessor="start"
          endAccessor="end"
          style={{
            height: window.innerWidth < 768 ? 
              (currentView === 'month' ? 400 : currentView === 'week' ? 500 : 600) : 
              window.innerWidth < 1024 ? 500 : 600
          }}
          onSelectSlot={handleSelectSlot}
          onSelectEvent={handleSelectEvent}
          onEventDrop={moveEvent}
          onEventResize={moveEvent}
          resizable={false}
          draggableAccessor={() => true}
          selectable={true}
          selectableAccessor={() => true}
          longPressThreshold={100}
          step={60}
          showMultiDayTimes={false}
          popup={false}
          popupOffset={30}
          className="calendar-hover-pointer"
          view={currentView}
          onView={handleViewChange}
          date={currentDate}
          onNavigate={handleNavigate}
          views={['month', 'week', 'day', 'agenda']}
          culture="es"
          messages={{
            next: "Siguiente",
            previous: "Anterior",
            today: "Hoy",
            month: "Mes",
            week: "Semana",
            day: "Día",
            agenda: "Agenda",
            date: "Fecha",
            time: "Hora",
            event: "Evento",
            allDay: "Todo el día",
            work_week: "Semana laboral",
            yesterday: "Ayer",
            tomorrow: "Mañana",
            noEventsInRange: "No hay eventos en este rango.",
            showMore: total => `+ Ver más (${total})`,
            dayNames: ["Domingo", "Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado"],
            dayNamesShort: ["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"]
          }}
          eventPropGetter={eventPropGetter}
        />
        </MobileCalendarWrapper>
      </div>

      {/* Lista de citas alternativa (visible cuando se selecciona la vista "agenda") */}
      {currentView === 'agenda' && (
        <div className="bg-white p-6 rounded-lg shadow mt-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Listado de Citas</h2>
          {citasOrdenadas.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Fecha y Hora
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Paciente
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Tratamiento
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Estado
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Acciones
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {citasOrdenadas.map((cita) => {
                    // Validar fecha y hora
                    let fechaFormateada = 'Fecha inválida';
                    let horaFormateada = 'Hora inválida';
                    let fechaValida = true;

                    try {
                      // Formatear fecha y hora para mostrar
                      const fechaHora = `${cita.fecha}T${cita.hora}`;
                      const fechaObj = new Date(fechaHora);

                      if (!isNaN(fechaObj.getTime())) {
                        fechaFormateada = formatDate(fechaObj, 'dd/MM/yyyy');
                        horaFormateada = formatDate(fechaObj, 'HH:mm');
                      } else {
                        fechaValida = false;
                        console.warn('Fecha/hora inválida para cita:', cita);
                      }
                    } catch (error) {
                      fechaValida = false;
                      console.error('Error al formatear fecha/hora:', error, cita);
                    }

                    // Definir clase según el estado
                    let estadoClase = "bg-blue-100 text-blue-800"; // Por defecto (reservada)
                    if (cita.estado === 'confirmada') estadoClase = "bg-green-100 text-green-800";
                    else if (cita.estado === 'completada') estadoClase = "bg-green-100 text-green-800";
                    else if (cita.estado === 'cancelada') estadoClase = "bg-red-100 text-red-800";

                    // Si la fecha es inválida, mostrar una fila de error
                    if (!fechaValida) {
                      return (
                        <tr key={cita.id} className="bg-red-50">
                          <td colSpan="5" className="px-6 py-4">
                            <div className="text-sm text-red-600">
                              Cita con formato inválido (ID: {cita.id}) - Fecha: {cita.fecha}, Hora: {cita.hora}
                            </div>
                          </td>
                        </tr>
                      );
                    }

                    return (
                      <tr key={cita.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">{fechaFormateada}</div>
                          <div className="text-sm text-gray-500">{horaFormateada}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">{cita.paciente_nombre || 'Sin nombre'}</div>
                          <div className="text-sm text-gray-500">{cita.paciente_rut || 'Sin RUT'}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{cita.tipo_tratamiento || cita.tratamiento_nombre || 'No especificado'}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${estadoClase}`}>
                            {cita.estado ? (cita.estado.charAt(0).toUpperCase() + cita.estado.slice(1)) : 'Desconocido'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <button
                            onClick={() => {
                              setSelectedCita(cita);
                              setShowDetailModal(true);
                            }}
                            className="text-green-600 hover:text-green-900 mr-3"
                          >
                            Ver
                          </button>
                          <button
                            onClick={() => handleEditClick(cita)}
                            className="text-amber-600 hover:text-amber-900 mr-3"
                          >
                            Editar
                          </button>
                          <button
                            onClick={() => handleDeleteConfirm(cita)}
                            className="text-red-600 hover:text-red-900"
                          >
                            Eliminar
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <NoCitas />
          )}
        </div>
      )}

      {/* Modal para formulario de nueva cita — extraído a CitaFormModal */}
      <CitaFormModal
        isOpen={showForm}
        mode="create"
        onClose={() => setShowForm(false)}
        onSubmit={handleSubmit}
        formData={formData}
        setFormData={setFormData}
        pacientes={pacientes}
        tratamientos={tratamientos}
        profesionales={profesionales}
        horariosDisponibles={horariosDisponibles}
        horasOcupadas={horasOcupadas}
        selectedDate={selectedDate}
      />

      {/* Modal para editar cita — extraído a CitaFormModal */}
      <CitaFormModal
        isOpen={showEditModal}
        mode="edit"
        onClose={() => setShowEditModal(false)}
        onSubmit={handleUpdateCita}
        formData={editFormData}
        setFormData={setEditFormData}
        pacientes={pacientes}
        tratamientos={tratamientos}
        horariosDisponibles={horariosDisponibles}
        horasOcupadas={horasOcupadas}
        originalHora={editFormData.original_hora}
        citas={citas}
        getDuracionFromTratamiento={getDuracionFromTratamiento}
      />

      {/* Modal de confirmación para eliminar cita */}
      {showDeleteConfirm && selectedCita && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center" onClick={(e) => e.target === e.currentTarget && setShowDeleteConfirm(false)}>
          <div className="relative p-6 border w-full max-w-md shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold text-gray-900">
                Confirmar Eliminación
              </h3>
              <button
                className="text-gray-500 hover:text-gray-700"
                onClick={() => setShowDeleteConfirm(false)}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-red-700">
                    ¿Está seguro que desea eliminar esta cita? Esta acción no se puede deshacer.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 p-4 rounded-md mb-4">
              <h4 className="text-lg font-medium text-gray-800 mb-2">Detalles de la Cita</h4>
              <div className="grid grid-cols-2 gap-2">
                <div className="text-sm font-medium text-gray-500">Paciente:</div>
                <div className="text-sm text-gray-900">{selectedCita.paciente_nombre || 'No disponible'}</div>

                <div className="text-sm font-medium text-gray-500">Fecha:</div>
                <div className="text-sm text-gray-900">
                  {formatDate(new Date(`${selectedCita.fecha}T${selectedCita.hora}`), 'dd/MM/yyyy')}
                </div>

                <div className="text-sm font-medium text-gray-500">Hora:</div>
                <div className="text-sm text-gray-900">{selectedCita.hora}</div>

                <div className="text-sm font-medium text-gray-500">Tratamiento:</div>
                <div className="text-sm text-gray-900">
                  {selectedCita.tipo_tratamiento || selectedCita.tratamiento_nombre || 'No especificado'}
                </div>
              </div>
            </div>

            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => setShowDeleteConfirm(false)}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleDeleteCita}
                className="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600"
              >
                Eliminar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal para detalles de la cita — extraído a CitaDetailModal */}
      {showDetailModal && (
        <CitaDetailModal
          cita={selectedCita}
          onClose={() => setShowDetailModal(false)}
          onEstadoChange={handleQuickStateChange}
          onEdit={handleEditClick}
          onDelete={handleDeleteConfirm}
        />
      )}
      
      {/* Modal de Confirmación para Completar Cita */}
      {showCompletarConfirm && selectedCita && (
        <div className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm overflow-y-auto h-full w-full z-[60] flex items-center justify-center p-4" onClick={(e) => e.target === e.currentTarget && setShowCompletarConfirm(false)}>
          <div className="relative w-full max-w-md bg-white rounded-2xl shadow-xl overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-green-500 to-green-600 px-6 py-4">
              <h3 className="text-xl font-bold text-white flex items-center gap-2">
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Finalizar Cita
              </h3>
            </div>
            
            <div className="p-6">
              {/* Warning */}
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
                <div className="flex gap-3">
                  <svg className="w-6 h-6 text-amber-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  <div>
                    <p className="font-semibold text-amber-800 mb-1">¿Finalizar esta cita?</p>
                    <p className="text-sm text-amber-700">
                      Al marcar como <strong>Completada</strong>, podrás crear la ficha clínica para registrar los insumos utilizados.
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Info de la cita */}
              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <p className="text-gray-500">Paciente</p>
                    <p className="font-semibold">{selectedCita.paciente_nombre}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Tratamiento</p>
                    <p className="font-semibold">{selectedCita.tipo_tratamiento || selectedCita.tratamiento_nombre}</p>
                  </div>
                </div>
              </div>
              
              {/* Botones */}
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowCompletarConfirm(false);
                    setEstadoPendiente(null);
                  }}
                  className="flex-1 px-4 py-2.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-medium transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={() => ejecutarCambioEstado('completada')}
                  className="flex-1 px-4 py-2.5 bg-green-500 text-white rounded-lg hover:bg-green-600 font-medium transition-colors flex items-center justify-center gap-2"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Sí, Finalizar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Redirección a Ficha Clínica */}
      {showRedirectToFicha && selectedCita && (
        <div className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm overflow-y-auto h-full w-full z-[60] flex items-center justify-center p-4" onClick={(e) => e.target === e.currentTarget && setShowRedirectToFicha(false)}>
          <div className="relative w-full max-w-md bg-white rounded-2xl shadow-xl overflow-hidden">
            {/* Header Verde */}
            <div className="bg-gradient-to-r from-green-500 to-green-600 px-6 py-4">
              <h3 className="text-xl font-bold text-white flex items-center gap-2">
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                ¡Cita Finalizada!
              </h3>
            </div>
            
            <div className="p-6">
              {/* Info */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-5">
                <div className="flex gap-3">
                  <svg className="w-6 h-6 text-blue-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div>
                    <p className="font-semibold text-blue-800 mb-1">Registrar Insumos Utilizados</p>
                    <p className="text-sm text-blue-700">
                      El inventario <strong>NO</strong> se ha descontado aún. ¿Deseas crear la ficha clínica ahora para registrar los insumos utilizados?
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Info de la cita */}
              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <p className="text-gray-500">Paciente</p>
                    <p className="font-semibold">{selectedCita.paciente_nombre}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Tratamiento</p>
                    <p className="font-semibold">{selectedCita.tipo_tratamiento || selectedCita.tratamiento_nombre}</p>
                  </div>
                </div>
              </div>
              
              {/* Botones */}
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowRedirectToFicha(false);
                    setShowDetailModal(false);
                  }}
                  className="flex-1 px-4 py-2.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-medium transition-colors"
                >
                  Más tarde
                </button>
                <button
                  onClick={() => {
                    setShowRedirectToFicha(false);
                    setShowDetailModal(false);
                    // Redirigir a fichas con parámetros
                    navigate(`/app/fichas-clinicas?nuevo=true&cita_id=${selectedCita.id}&paciente_rut=${selectedCita.paciente_rut}&tratamiento_id=${selectedCita.tratamiento_id || ''}`);
                  }}
                  className="flex-1 px-4 py-2.5 bg-[#2A9D8F] text-white rounded-lg hover:bg-[#227f74] font-medium transition-colors flex items-center justify-center gap-2"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Sí, ir a Ficha
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default AdminCitasPage; 
